import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ElementInViewport } from '../../../common/components/elementInViewport';
import { SimilarOffers } from '../../../components/similar_offers/SimilarOffers';
import { hasWithoutCompetitors } from '../../../components/similar_offers/utils/hasWithoutCompetitors';
import {
  EtrackOfferClicksLabels,
  EtrackOffersShowActions,
  trackOffersShow,
  TProductPartial,
} from '../../../ebc/track_similar';
import {
  similarOffersSelector,
  abGroupSelector,
  offerDataSelector,
  selectIsSimilarOffersForAgencyBrandingAvailable,
} from '../../../selectors';
import { requestSimilarOffers, TModelType } from '../../../store/similar';
import { getVariants } from '../../../utils/ebc';
import { getPageEBC } from '../../../utils/tracking/get_page_ebc';
import { getUserEBC } from '../../../utils/tracking/get_user_ebc';
import { SimilarOffersTitleContainer } from '../SimilarOffersTitle';

interface ISimilarOffersContainerProps {
  disableExternalContainerStyles?: boolean;
  modelType: TModelType;
  slidesToShow?: number;
}

export const SimilarOffersContainer: React.VFC<ISimilarOffersContainerProps> = ({
  disableExternalContainerStyles,
  modelType,
  slidesToShow,
}: ISimilarOffersContainerProps) => {
  const dispatch = useDispatch();
  const similarOffers = useSelector(similarOffersSelector);
  const withoutCompetitors = hasWithoutCompetitors(similarOffers);
  const abGroup = useSelector(abGroupSelector);
  const offerData = useSelector(offerDataSelector);
  const isBrandingAvailable = useSelector(selectIsSimilarOffersForAgencyBrandingAvailable);

  const [wasShown, setWasShown] = React.useState(false);

  let title: string | React.ReactNode = 'Рекомендации по истории поиска';

  if (withoutCompetitors) {
    title = isBrandingAvailable ? <SimilarOffersTitleContainer /> : 'Другие предложения этого продавца';
  }

  const onRequestSimilarOffers = React.useCallback(
    () =>
      dispatch(
        requestSimilarOffers({
          offerId: offerData.offer.cianId,
          modelType,
          publishedUserId: offerData.offer.publishedUserId,
        }),
      ),
    [dispatch, modelType, offerData],
  );

  const modelVersion = similarOffers[modelType].modelVersion;
  const similarOffersList = similarOffers[modelType].list;
  const user = React.useMemo(() => getUserEBC(offerData.user, abGroup), [offerData, abGroup]);
  const page = React.useMemo(() => getPageEBC(offerData.offer.id, offerData.breadcrumbs), [offerData]);

  const products = React.useMemo(
    () =>
      similarOffersList.map((similarOffer, index): TProductPartial => {
        let variant;

        if (similarOffer.gaVariant) {
          variant = getVariants(similarOffer.gaVariant);
        }

        return {
          id: similarOffer.cianId,
          position: index + 1,
          ownerId: similarOffer.publishedUserId || undefined,
          variant,
        };
      }),
    [similarOffersList],
  );

  const onVisibilityChange = React.useCallback(
    (isVisible: boolean) => {
      const eventAction = withoutCompetitors
        ? EtrackOffersShowActions.show_others_offers
        : EtrackOffersShowActions.show_similar_offers;

      if (isVisible && !wasShown && products.length) {
        setWasShown(true);

        trackOffersShow({
          action: eventAction,
          label: !withoutCompetitors ? EtrackOfferClicksLabels[modelType] : undefined,
          products,
          modelVersion,
          user,
          page,
        });
      }
    },
    [modelVersion, modelType, wasShown, products, user, page, withoutCompetitors],
  );

  return (
    <ElementInViewport onChange={onVisibilityChange} viewPercent={0.7}>
      <SimilarOffers
        agent={offerData.agent}
        offer={offerData.offer}
        similarOffers={similarOffersList}
        withoutCompetitors={withoutCompetitors}
        modelVersion={modelVersion}
        modelType={modelType}
        user={user}
        page={page}
        requestSimilarOffers={onRequestSimilarOffers}
        disableExternalContainerStyles={disableExternalContainerStyles}
        slidesToShow={slidesToShow}
        title={title}
      />
    </ElementInViewport>
  );
};
